// Public company signup — first user becomes company_admin +
// billing_owner with a 14-day trial. Full-bleed brand treatment that
// mirrors the Kanriva™ login screen.
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Building2, Lock, Check, User, Mail, Factory } from 'lucide-react';

const LOGO_URL = '/brand/kanriva-logo.png';
const SIGNUP_BG = '/brand/login-bg-alt.jpg';

const LIMITS = [
  { ok: true,  label: 'Workfloor (Picking, Expedition, Damage Log)', detail: 'Full access' },
  { ok: true,  label: 'Teams', detail: 'View-only during trial' },
  { ok: true,  label: 'Projects', detail: '1 project during trial' },
  { ok: true,  label: 'Checklists', detail: 'First 3 you create' },
  { ok: true,  label: 'Exports', detail: '1 per 7 days during trial' },
  { ok: false, label: 'Archiving', detail: 'Unlocked after upgrade' },
];

const FIELD_CLASS =
  'h-12 rounded-xl border-white/12 bg-white/[0.06] pl-11 text-white placeholder:text-white/25 transition-colors focus:border-cyan-400/60 focus:bg-white/[0.09] focus-visible:ring-cyan-400/30';
const LABEL_CLASS = 'text-xs font-medium uppercase tracking-wider text-white/45';

export default function SignupPage() {
  const navigate = useNavigate();
  const { signupCompany } = useAuth();
  const [form, setForm] = useState({
    company_name: '', full_name: '', email: '', password: '', industry: '',
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await signupCompany(form);
      toast.success('Welcome! Your 14-day trial just started.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message || 'Signup failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#050a14]" data-testid="signup-page">
      {/* Full-bleed brand backdrop */}
      <img
        src={SIGNUP_BG}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover object-center scale-105 animate-[kenburns_36s_ease-in-out_infinite_alternate]"
      />
      <div className="absolute inset-0 bg-[#050a14]/74" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050a14] via-[#050a14]/45 to-[#050a14]/85" />
      <div className="absolute inset-0 bg-[radial-gradient(120%_90%_at_50%_-10%,rgba(37,99,235,0.28),transparent_60%)]" />

      <div className="relative z-10 flex min-h-screen flex-col">
        <header className="flex items-center justify-between px-6 py-6 sm:px-10">
          <Link
            to="/"
            className="group flex items-center gap-2 text-sm font-medium text-white/55 transition-colors hover:text-white"
            data-testid="signup-back-home"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            Back to home
          </Link>
          <p className="hidden text-[10px] uppercase tracking-[0.22em] text-white/35 sm:block">
            powered by Kaizen Kanri
          </p>
        </header>

        <main className="flex flex-1 items-center justify-center px-4 pb-12 sm:px-6">
          <div className="grid w-full max-w-5xl gap-10 lg:grid-cols-[1fr_440px] lg:gap-16">
            {/* Pitch */}
            <div className="animate-[fadeIn_0.8s_ease-out_both] lg:pt-4">
              <span className="inline-flex items-start">
                <img src={LOGO_URL} alt="Kanriva™" className="h-9 sm:h-10" />
              </span>
              <h1 className="mt-7 text-4xl font-semibold leading-[1.1] tracking-tight text-white sm:text-5xl">
                Start your free
                <br />
                <span className="text-cyan-300">14-day trial</span>
              </h1>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-white/55 sm:text-base">
                Bring planning, presence and improvement into one warehouse-grade workspace —
                picking, expedition, damage and shift planning, on one login.
              </p>

              <div className="mt-8 max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-xl">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/60">
                    What's in the trial
                  </span>
                  <span className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-cyan-200">
                    14 days · no card
                  </span>
                </div>
                <ul className="space-y-2 text-sm" data-testid="signup-trial-limits">
                  {LIMITS.map((l, i) => (
                    <li key={i} className="flex items-start gap-2">
                      {l.ok
                        ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />
                        : <Lock className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />}
                      <div className="leading-snug">
                        <span className="text-white/80">{l.label}</span>
                        <span className="text-white/40"> — {l.detail}</span>
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-[11px] leading-relaxed text-white/40">
                  Pricing is modular and depends on company size. After signup, email{' '}
                  <a href="mailto:info@kaizenkanri.nl" className="text-cyan-300 hover:text-cyan-200">info@kaizenkanri.nl</a>{' '}
                  to discuss what your team needs.
                </p>
              </div>
            </div>

            {/* Form */}
            <div
              className="animate-[fadeIn_1s_ease-out_both] rounded-2xl border border-white/12 bg-[#070d1a]/60 p-6 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] backdrop-blur-2xl sm:p-7"
              data-testid="signup-card"
            >
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-company" className={LABEL_CLASS}>Company name</Label>
                  <div className="relative">
                    <Building2 className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
                    <Input
                      id="signup-company"
                      required
                      value={form.company_name}
                      onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                      placeholder="Acme Logistics"
                      className={FIELD_CLASS}
                      data-testid="signup-company"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-fullname" className={LABEL_CLASS}>Your full name</Label>
                  <div className="relative">
                    <User className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
                    <Input
                      id="signup-fullname"
                      required
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      placeholder="Jane Doe"
                      className={FIELD_CLASS}
                      data-testid="signup-fullname"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-email" className={LABEL_CLASS}>Work email</Label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
                    <Input
                      id="signup-email"
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="jane@acme.com"
                      className={FIELD_CLASS}
                      data-testid="signup-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-password" className={LABEL_CLASS}>Password</Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-white/35" />
                    <PasswordInput
                      id="signup-password"
                      required
                      minLength={8}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder="At least 8 characters"
                      className={FIELD_CLASS}
                      testId="signup-password"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-industry" className={LABEL_CLASS}>Industry (optional)</Label>
                  <div className="relative">
                    <Factory className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
                    <Input
                      id="signup-industry"
                      value={form.industry}
                      onChange={(e) => setForm({ ...form, industry: e.target.value })}
                      placeholder="Logistics, Manufacturing…"
                      className={FIELD_CLASS}
                      data-testid="signup-industry"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="h-12 w-full rounded-xl bg-cyan-500 text-[15px] font-semibold text-[#04141a] transition-all hover:bg-cyan-400 hover:shadow-[0_0_36px_-8px_rgba(34,211,238,0.65)]"
                  disabled={busy}
                  data-testid="signup-submit"
                >
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Start free 14-day trial
                </Button>
              </form>

              <div className="mt-6 border-t border-white/10 pt-5 text-center">
                <p className="text-[11px] leading-relaxed text-white/40">
                  By signing up you become the company billing owner. We never charge during the trial.
                </p>
                <p className="mt-2 text-[11px] leading-relaxed text-white/40" data-testid="signup-language-note">
                  Kanriva™, onboarding and support are in English. Dutch correspondence, documentation and contracts are available in writing — we prefer to say that up front.
                </p>
                <p className="mt-2 text-xs text-white/40">
                  Already have an account?{' '}
                  <Link to="/login" className="font-medium text-cyan-300 hover:text-cyan-200" data-testid="signup-login-link">
                    Sign in
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </main>

        <footer className="px-6 pb-8 sm:px-10 sm:pb-10">
          <div className="max-w-xl animate-[fadeIn_1.2s_ease-out_both] border-l-2 border-cyan-400/50 pl-4 sm:pl-5">
            <blockquote className="text-sm font-light leading-relaxed text-white/70 sm:text-base">
              “Excellence is not a destination but a continuous journey that never ends.”
            </blockquote>
            <p className="mt-2 text-xs uppercase tracking-[0.18em] text-cyan-300/70">— Brian Tracy</p>
          </div>
        </footer>
      </div>
    </div>
  );
}
